import { FC } from 'react';

// Third-party
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import SvgIcon from '@mui/material/SvgIcon';
import Stack from '@mui/material/Stack';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import moment from 'moment';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material';

// Utils
import { useTranslation } from 'react-i18next';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { getTimezone } from '@mgm/common/utils';
import { MemoizedBaseAvatar, BaseTooltip } from '@mgm/common/components/base';
import { isBeforeToday } from '../../../../utils/helpers';

// Components

// Hooks
import { useAppDispatch } from '../../../../hooks/useAppStore';

// Redux-toolkit
import { commonActions } from '../../../../store/common/slice';

// Assets
import { ReactComponent as noDataIcon } from '../../../../static/icons/tasklists/ic_no_data.svg';
import { ReactComponent as TodayIcon } from '../../../../static/icons/ic_today.svg';
import { ReactComponent as ViewIcon } from '../../../../static/icons/ic_view.svg';

// Types
import { TTasklistTemplateList } from '../type';

const TasklistTemplateList: FC<TTasklistTemplateList> = ({
  tasklists,
  selectedTasklist,
  setSelectedTemplate,
}) => {
  const theme = useTheme() as TCustomTheme;
  const { t } = useTranslation('tasklists');
  const dispatch = useAppDispatch();
  const { isMobile } = useCustomTheme(theme);

  const openTasklistNav = () => {
    dispatch(commonActions.setIsOpenPagesNav(true));
  };

  return (
    <Box>
      {tasklists?.length ? (
        <Box>
          {tasklists?.map((tasklist) => (
            <ListItem
              sx={{
                mt: 1,
                ':hover': {
                  background: 'rgba(168, 85, 247, 0.16)',
                  borderRadius: 0,
                },
                ...(tasklist.id === selectedTasklist.id && {
                  background: 'rgba(168, 85, 247, 0.16)',
                  borderRadius: 0,
                }),
                ...(isMobile && {
                  borderRadius: 0,
                }),
              }}
              secondaryAction={(
                <Box>
                  <SvgIcon
                    onClick={() => setSelectedTemplate(tasklist)}
                    className="cursor-pointer"
                    component={ViewIcon}
                  />
                  <Stack>
                    {(tasklist.assigneeAvatar
                                      || tasklist.assigneeName)
                                  && (
                                  <BaseTooltip tooltipText={tasklist.assigneeName}>
                                    <MemoizedBaseAvatar
                                      size={{
                                        width: 22,
                                        height: 22,
                                      }}
                                      sx={{
                                        marginRight: '0',
                                      }}
                                      avatarFile={tasklist.assigneeAvatar || null}
                                      avatarName={tasklist.assigneeName || ''}
                                    />
                                  </BaseTooltip>
                                  )}
                  </Stack>
                </Box>
                      )}
              key={tasklist.id}
              alignItems="flex-start"
            >
              <ListItemAvatar>
                <MemoizedBaseAvatar
                  avatarFile={tasklist.creator.avatarFile}
                  avatarName={tasklist.creator.name}
                />
              </ListItemAvatar>
              <ListItemText
                sx={{
                  maxWidth: 250,
                }}
                primary={(
                  <Stack direction="row">
                    {tasklist.title}
                    {
                      tasklist.isDated
                      && tasklist.date && (
                      <BaseTooltip tooltipText={
                        isBeforeToday(tasklist.date)
                          ? (
                            <Typography variant="body2">
                              {t('theDueDate')}
                            </Typography>
                          )
                          : moment.tz(tasklist.date, getTimezone()).format('MMM Do YYYY')
}
                      >
                        <SvgIcon
                          fontSize="small"
                          sx={{
                            ml: 1,
                          }}
                          color={isBeforeToday(tasklist.date) ? 'error' : 'primary'}
                          component={TodayIcon}
                        />
                      </BaseTooltip>
                      )
                     }
                  </Stack>
                        )}
                secondary={(
                  <>
                    <Typography variant="body2" display="block" component="span">
                      {tasklist.description}
                    </Typography>
                    {tasklist.updatedAt
                    && (
                    <>
                      <Typography
                        component="span"
                        sx={{
                          color: theme.palette.primary.main,
                          pr: 1,
                        }}
                        variant="caption"
                      >
                        {t('lastUpdated')}
                        :
                      </Typography>
                      <Typography component="span" variant="caption">
                        {moment(tasklist.updatedAt).format('MMM Do YYYY HH:mm:ss')}
                      </Typography>
                    </>
                    )}
                  </>
                )}
              />
            </ListItem>
          ))}
        </Box>
      ) : (
        <Box
          marginX="auto"
          marginTop={10}
          sx={{
            width: 200,
            height: 200,
            lineHeight: 0,
            borderRadius: '50%',
            bgcolor: 'background.neutral',
          }}
        >
          <SvgIcon
            sx={{
              width: '100%',
              height: '100%',
            }}
            component={noDataIcon}
            inheritViewBox
          />
          <Box marginTop={4}>
            <Typography textAlign="center" variant="subtitle2">{t('youHaventAdded')}</Typography>
            <Button onClick={openTasklistNav} fullWidth variant="contained" sx={{ mt: 2 }}>{t('addTasklist')}</Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export {
  TasklistTemplateList,
};
